import type { ReactNode } from "react";
import Paper from "@mui/material/Paper";
import { useFormContext } from "react-hook-form";
import { SchemaField, type FieldConfig } from "../../../components/forms/SchemaField";
import { FormSection } from "../../../components/forms/FormSection";
import { useIsPhone } from "../../../hooks/useIsPhone";
import type { UserEditFormData } from "../../../schemas/userProfileSchema";

const NOTES_FIELD: FieldConfig<UserEditFormData> = {
  name: "notes",
  label: "Internal Notes",
  type: "textarea",
  rows: 8,
  placeholder:
    "Private notes about this person (visible to internal team only)...",
};

/**
 * Pure section component. Reads/writes through the page-level RHF context.
 */
export function InternalNotesTab({
  editable,
  testIdPrefix,
  hubspotSection,
}: {
  editable: boolean;
  /** data-testid prefix (e.g. "user-create") for E2E selector stability. */
  testIdPrefix?: string;
  /**
   * The detail page's HubSpot link-status block (linked state + unlink,
   * ADR-172) — the link pointer lives in personInternals, so this tab
   * is its home. Omitted on the create page.
   */
  hubspotSection?: ReactNode;
}) {
  const isPhone = useIsPhone();
  const { control } = useFormContext<UserEditFormData>();

  return (
    <Paper sx={{ p: isPhone ? 2 : 3 }}>
      {hubspotSection}
      <FormSection title="Notes">
        <SchemaField
          config={{
            ...NOTES_FIELD,
            disabled: !editable,
            testId: testIdPrefix
              ? `${testIdPrefix}-internalNotes-input`
              : undefined,
            helperText: editable
              ? undefined
              : "Visible to internal team members. Only organizers and admins can edit.",
          }}
          control={control}
        />
      </FormSection>
    </Paper>
  );
}
